import React, { useCallback, useEffect, useState } from 'react';
import { IDefaultResponsiveSupport } from '../../common/interfaces/common.interface';
import useSpeechRecognition from '../../hooks/speech-recognition/speech-recognition.hook';
import useSpeechSynthesis from '../../hooks/speech-synthesis/speech-synthesis.hook';
import styles from './ai-assistant.module.scss';
import MicComponent from '../mic/mic.component';
import {
  classnames,
  debounce,
} from '../../common/general-utilities/general-utilities';
import { qnaService } from '../../common/services/q-n-a.service';
import ResponsiveModeService from '../../common/services/responsive-mode.service';

interface IAiAssistantComponent extends IDefaultResponsiveSupport {
  className?: string;
}

const AiAssistantComponent: React.FC<IAiAssistantComponent> = ({
  className,
  responsiveMode,
  osMode,
}) => {
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [aiResponse, setAIResponse] = useState('');

  const {
    transcript,
    interimTranscript,
    listening,
    intensity,
    startListening,
    stopListening,
  } = useSpeechRecognition();
  const { speak, cancelSpeak, speaking } = useSpeechSynthesis();

  useEffect(() => {
    if (speaking) {
      setLoadingAnswer(false);
    } else {
      setAIResponse('');
    }
  }, [speaking]);

  const startTheSpeech = useCallback(
    (message: string) => {
      setTimeout(() => speak(message), 500);
    },
    [speak],
  );

  useEffect(() => {
    if (transcript && qnaService && startTheSpeech) {
      console.log('Transcript:', transcript);
      setLoadingAnswer(true);
      const response = qnaService.getBestResponse(transcript);
      const message = response
        ?.replace(/\n/g, ' ')
        .replace(/\t/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      setAIResponse(message);
      startTheSpeech(message);
    }
  }, [startTheSpeech, transcript]);

  const micOnClick = () => {
    cancelSpeak();
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const debouncedMicOnClick = debounce(micOnClick, 300);

  return (
    <div
      className={classnames(
        styles.aiAssistantContainer,
        ResponsiveModeService.responsiveModeClass(styles, responsiveMode),
        { [styles.osMode]: osMode,}
      )}
    >
      {(listening || loadingAnswer || speaking) &&
        (interimTranscript || transcript || aiResponse) && (
          <div
            className={classnames(styles.transcriptContainer, 'transcriptContainerGlobal', {
              [styles.speaking]: speaking,
              [styles.loadingAnswer]: loadingAnswer,
            })}
          >
            <p>
              {listening
                ? interimTranscript
                : loadingAnswer
                  ? transcript
                  : speaking
                    ? aiResponse
                    : ''}
            </p>
          </div>
        )}
      <MicComponent
        className={className}
        isActive={listening}
        onClick={debouncedMicOnClick}
        intensity={intensity}
        loading={loadingAnswer}
        aiAnswering={speaking}
        responsiveMode={responsiveMode}
        osMode={osMode}
      />
    </div>
  );
};

export default AiAssistantComponent;
